/**
 * StepSubmission Component
 *
 * Final step of the story creation wizard.
 * Shows summary, handles submission, and displays success state.
 */

import { useRouter } from 'next/router'
import { useState } from 'react'

import { useAccountCharactersList } from '@/shared/api/generated/characters/characters'
import { useLanguagesList } from '@/shared/api/generated/languages/languages'
import { PlotOutline, PremadeStoryConceptDetail, SessionSubmitResponse, StoryCreationSession } from '@/shared/api/models'
import { Button } from '@/shared/components/Button/Button'
import { Icon } from '@/shared/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faBook } from '@fortawesome/pro-light-svg-icons/faBook'
import { faCheckCircle } from '@fortawesome/pro-light-svg-icons/faCheckCircle'
import { faCoins } from '@fortawesome/pro-light-svg-icons/faCoins'
import { faGlobe } from '@fortawesome/pro-light-svg-icons/faGlobe'
import { faPalette } from '@fortawesome/pro-light-svg-icons/faPalette'
import { faUsers } from '@fortawesome/pro-light-svg-icons/faUsers'
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles'
import { Alert, Box, CircularProgress, Divider, styled, Typography } from '@mui/material'

import { ArtStyleSelector } from '../components/ArtStyleSelector'
import { SelectedCharacters } from '../types'

interface StepSubmissionProps {
  selectedCharacters: SelectedCharacters
  plot: PlotOutline | null
  selectedStyleId: number | null
  selectedLanguageCode: string
  session: StoryCreationSession | null
  onSubmit: () => Promise<SessionSubmitResponse>
  onSelectStyle: (styleId: number) => void
  isSubmitting?: boolean
  isUpdating?: boolean
  error?: string | null
  onStartNewStory?: () => void
  isPremadePath?: boolean
  selectedConcept?: PremadeStoryConceptDetail | null
}

export const StepSubmission: React.FC<StepSubmissionProps> = ({
  selectedCharacters,
  plot,
  selectedStyleId,
  selectedLanguageCode,
  session,
  onSubmit,
  onSelectStyle,
  isSubmitting = false,
  isUpdating = false,
  error = null,
  onStartNewStory,
  isPremadePath = false,
  selectedConcept = null,
}) => {
  const router = useRouter()
  const [isSuccess, setIsSuccess] = useState(false)

  const { data: characters } = useAccountCharactersList()
  const { data: languagesData } = useLanguagesList()
  const selectedLanguage = languagesData?.results?.find((l) => l.code === selectedLanguageCode) ?? null

  const mainCharacters =
    characters?.filter((c) => selectedCharacters.mainCharacterIds.includes(c.id)) || []
  const supportingCharacters =
    characters?.filter((c) => selectedCharacters.supportingCharacterIds.includes(c.id)) || []

  const handleSubmit = async () => {
    try {
      const result = await onSubmit()

      setIsSuccess(true)

      // Redirect to the created story (or library as fallback) after a short delay
      const redirectUrl = result?.story_id ? `/library/${result.story_id}` : '/library'

      setTimeout(() => {
        router.push(redirectUrl)
      }, 2000)
    } catch {
      // Error is set in session hook, user sees the Alert below
    }
  }

  if (isSuccess) {
    return (
      <StyledSuccessContainer>
        <StyledSuccessIcon>
          <Icon icon={faCheckCircle} />
        </StyledSuccessIcon>
        <Typography variant="h5" gutterBottom>
          Story Creation Started!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your personalized story is being created. We&apos;ll notify you when it&apos;s ready!
        </Typography>
        <CircularProgress size={24} />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          Redirecting to your library...
        </Typography>
      </StyledSuccessContainer>
    )
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <Typography variant="h5" component="h2">
          Review & Create Your Story
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Confirm your selections before creating the story
        </Typography>
      </StyledHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {session && !session.is_ready_for_submission && !error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This session is not yet ready for submission. Please complete all previous steps first.
        </Alert>
      )}

      {session && !session.is_ready_for_submission && onStartNewStory && (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Button onClick={onStartNewStory}>
            Start a New Story
          </Button>
        </Box>
      )}

      <StyledSummaryCard>
        {/* Characters Section */}
        <StyledSection>
          <StyledSectionTitle>
            <Icon icon={faUsers} />
            Characters
          </StyledSectionTitle>

          <StyledCharacterList>
            <Typography variant="body2" fontWeight={500}>
              Main Characters:
            </Typography>
            {mainCharacters.map((c) => (
              <StyledCharacterChip key={c.id}>{c.name}</StyledCharacterChip>
            ))}
          </StyledCharacterList>

          {supportingCharacters.length > 0 && (
            <StyledCharacterList>
              <Typography variant="body2" fontWeight={500}>
                Supporting:
              </Typography>
              {supportingCharacters.map((c) => (
                <StyledCharacterChip key={c.id} $supporting>
                  {c.name}
                </StyledCharacterChip>
              ))}
            </StyledCharacterList>
          )}
        </StyledSection>

        <Divider />

        {/* Plot / Premade Story Section */}
        {isPremadePath ? (
          <StyledSection>
            <StyledSectionTitle>
              <Icon icon={faBook} />
              Premade Story
            </StyledSectionTitle>

            {selectedConcept ? (
              <>
                <Typography variant="subtitle1" fontWeight={600}>
                  {selectedConcept.title}
                </Typography>
                {selectedConcept.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedConcept.description}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Premade story selected
              </Typography>
            )}
          </StyledSection>
        ) : (
          <StyledSection>
            <StyledSectionTitle>
              <Icon icon={faBook} />
              Story Plot
            </StyledSectionTitle>

            {plot ? (
              <>
                <Typography variant="subtitle1" fontWeight={600}>
                  {plot.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {plot.quick_summary || plot.plot_narrative?.slice(0, 200) + '...'}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No plot selected
              </Typography>
            )}
          </StyledSection>
        )}

        <Divider />

        {/* Language Section */}
        {selectedLanguage && selectedLanguageCode !== 'en' && (
          <>
            <StyledSection>
              <StyledSectionTitle>
                <Icon icon={faGlobe} />
                Language
              </StyledSectionTitle>

              <Typography variant="subtitle1" fontWeight={600}>
                {selectedLanguage.name_english}
                {selectedLanguage.name_english !== selectedLanguage.name_native && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({selectedLanguage.name_native})
                  </Typography>
                )}
              </Typography>
            </StyledSection>

            <Divider />
          </>
        )}

        {/* Art Style Section */}
        <StyledSection>
          <StyledSectionTitle>
            <Icon icon={faPalette} />
            Art Style
          </StyledSectionTitle>

          <ArtStyleSelector
            selectedStyleId={selectedStyleId}
            onSelectStyle={onSelectStyle}
            disabled={isUpdating || isSubmitting}
          />
        </StyledSection>
      </StyledSummaryCard>

      <StyledCreditInfo>
        <Icon icon={faCoins} />
        <Typography variant="body2">
          Creating this story will use <strong>200 credits</strong>
        </Typography>
      </StyledCreditInfo>

      <StyledFooter>
        <Button
          primary
          size="large"
          onClick={handleSubmit}
          disabled={isSubmitting || !session?.is_ready_for_submission}
          sx={{ minWidth: pxToRem(200) }}
        >
          {isSubmitting ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Creating...
            </>
          ) : (
            <>
              <Icon icon={faWandMagicSparkles} style={{ marginRight: 8 }} />
              Create My Story
            </>
          )}
        </Button>
      </StyledFooter>
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)`
  max-width: ${pxToRem(700)};
  margin: 0 auto;
`

const StyledHeader = styled(Box)`
  text-align: center;
  margin-bottom: ${pxToRem(24)};
`

const StyledSummaryCard = styled(Box)`
  background-color: ${(props) => props.theme.palette.background.paper};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: ${pxToRem(16)};
  padding: ${pxToRem(24)};
`

const StyledSection = styled(Box)`
  padding: ${pxToRem(16)} 0;
`

const StyledSectionTitle = styled(Typography)`
  display: flex;
  align-items: center;
  gap: ${pxToRem(8)};
  font-size: ${pxToRem(12)};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => props.theme.palette.text.secondary};
  margin-bottom: ${pxToRem(12)};
`

const StyledCharacterList = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${pxToRem(8)};
  margin-bottom: ${pxToRem(8)};
`

const StyledCharacterChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$supporting',
})<{ $supporting?: boolean }>`
  display: inline-block;
  padding: ${pxToRem(4)} ${pxToRem(12)};
  background-color: ${(props) =>
    props.$supporting ? props.theme.palette.grey[200] : props.theme.palette.primary.light}20;
  color: ${(props) =>
    props.$supporting ? props.theme.palette.text.secondary : props.theme.palette.primary.dark};
  border-radius: ${pxToRem(16)};
  font-size: ${pxToRem(13)};
`

const StyledCreditInfo = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${pxToRem(8)};
  padding: ${pxToRem(16)};
  background-color: ${(props) => props.theme.palette.warning.light}20;
  border-radius: ${pxToRem(8)};
  margin-top: ${pxToRem(24)};
  color: ${(props) => props.theme.palette.warning.dark};
`

const StyledFooter = styled(Box)`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: ${pxToRem(24)};
  margin-top: ${pxToRem(24)};
  border-top: 1px solid ${(props) => props.theme.palette.divider};
`

const StyledSuccessContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${pxToRem(64)} ${pxToRem(24)};
`

const StyledSuccessIcon = styled(Box)`
  font-size: ${pxToRem(64)};
  color: ${(props) => props.theme.palette.success.main};
  margin-bottom: ${pxToRem(24)};
`

export default StepSubmission
